import { motion } from 'framer-motion';
import { Cloud, Layers as LayersIcon, Mail, Network, Users as UsersIcon } from 'lucide-react';
import { PolarAngleAxis, RadialBar, RadialBarChart } from 'recharts';
import { useOrgStore } from '../store/useOrgStore';
import { EmptyState } from '../components/ui/EmptyState';
import { RiskBadge } from '../components/ui/RiskBadge';
import type { DataCloudReadiness } from '../types/org.types';

function colorFor(score: number): string {
  if (score >= 80) return '#3FA96B';
  if (score >= 60) return '#5B7CFF';
  if (score >= 40) return '#C7851A';
  return '#D64545';
}

function tierBadgeTone(tier: 'low' | 'mid' | 'high'): 'critical' | 'medium' | 'healthy' {
  return tier === 'high' ? 'healthy' : tier === 'mid' ? 'medium' : 'critical';
}

// Live audit store by default, which since TYR-216 is the client Hub alone;
// a stored client scan passes its own audit in.
// Passing `data` at all, null included, is scan mode: the store is not read.
export function DataCloudPage({ data }: { data?: DataCloudReadiness | null } = {}) {
  const storeDc = useOrgStore((s) => s.audit.datacloud);
  const scanMode = data !== undefined;
  const dc = scanMode ? data : storeDc;

  if (!dc)
    return (
      <EmptyState
        title="Data Cloud readiness not loaded"
        hint="Reconnect to scan again."
        icon={<Cloud size={20} />}
      />
    );

  const color = colorFor(dc.score);

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-adopt mb-1">Data Cloud Readiness</div>
        <h1 className="text-3xl font-semibold text-crux-light tracking-tight">Identity, Sources, Unification</h1>
        <p className="text-sm text-neutral-500 mt-1 max-w-3xl">
          Five composite signals that, together, surface every fragment of the unified-customer-profile
          conversation. The lower this score, the stronger the Data Cloud case.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
          className="bg-surface-1 border border-border rounded-lg p-6 lg:col-span-1 flex flex-col items-center justify-center"
        >
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
            Readiness Score
          </div>
          <div className="relative">
            <RadialBarChart
              width={220}
              height={220}
              cx={110}
              cy={110}
              innerRadius={86}
              outerRadius={104}
              startAngle={210}
              endAngle={-30}
              data={[{ name: 'score', value: dc.score, fill: color }]}
            >
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar background={{ fill: '#E0DED7' }} dataKey="value" cornerRadius={0} />
            </RadialBarChart>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-5xl font-bold tabular-nums" style={{ color }}>
                {dc.score}
              </div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">/ 100</div>
            </div>
          </div>
          <div className="mt-2">
            <RiskBadge tone={tierBadgeTone(dc.tier)}>
              {dc.tier === 'high' ? 'High' : dc.tier === 'mid' ? 'Mid' : 'Low'}
            </RiskBadge>
          </div>
        </motion.div>

        <div className="bg-surface-1 border border-border rounded-lg p-6 lg:col-span-2">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
            What this score means
          </div>
          <p className="text-sm text-crux-light/90 leading-relaxed">{dc.framing}</p>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
            <ScoreTile label="Duplicates" score={dc.signals.duplicates.score} icon={<Mail size={14} />} />
            <ScoreTile label="Overlap" score={dc.signals.overlap.score} icon={<UsersIcon size={14} />} />
            <ScoreTile
              label="Sources"
              score={dc.signals.externalSources.score}
              icon={<Network size={14} />}
            />
            <ScoreTile
              label="Marketing Cloud"
              score={dc.signals.marketingCloud.score}
              icon={<Cloud size={14} />}
            />
            <ScoreTile
              label="Customer 360"
              score={dc.signals.customer360.score}
              icon={<LayersIcon size={14} />}
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <SignalCard
          title="Duplicate Email Rate (Contacts + Leads)"
          score={dc.signals.duplicates.score}
          weight={20}
          body={
            <>
              <Stat label="Duplicate groups" value={dc.signals.duplicates.duplicateGroups.toLocaleString()} />
              <Stat label="Sample size" value={dc.signals.duplicates.totalSampled.toLocaleString()} />
              <Stat
                label="Duplicate %"
                value={`${dc.signals.duplicates.duplicatePct.toFixed(2)}%`}
              />
            </>
          }
          upsell="Data Cloud identity resolution"
        />
        <SignalCard
          title="Lead ↔ Contact Email Overlap"
          score={dc.signals.overlap.score}
          weight={20}
          body={
            <>
              <Stat label="Overlap records" value={dc.signals.overlap.overlapCount.toLocaleString()} />
              <Stat
                label="of leads sampled"
                value={`${dc.signals.overlap.leadSampled.toLocaleString()} (${dc.signals.overlap.overlapPct.toFixed(1)}%)`}
              />
            </>
          }
          upsell="Data Cloud identity resolution"
        />
        <SignalCard
          title="External Integration Sources"
          score={dc.signals.externalSources.score}
          weight={20}
          body={
            <>
              <Stat label="Named Credentials" value={dc.signals.externalSources.namedCredentials} />
              <Stat label="Connected Apps" value={dc.signals.externalSources.connectedApps} />
              <Stat label="Platform Events" value={dc.signals.externalSources.platformEvents} />
              <Stat label="Installed Packages" value={dc.signals.externalSources.installedPackages} />
            </>
          }
          upsell="Data Cloud data streams"
        />
        <SignalCard
          title="Marketing Cloud Connection"
          score={dc.signals.marketingCloud.score}
          weight={20}
          body={
            <>
              <Stat label="Connected" value={dc.signals.marketingCloud.connected} />
              <Stat label="Sync on" value={dc.signals.marketingCloud.syncOn} />
              <p className="text-xs text-neutral-500 col-span-full mt-1">{dc.signals.marketingCloud.detail}</p>
            </>
          }
          upsell="Data Cloud: MC activation"
        />
        <SignalCard
          title="Customer 360 Completeness"
          score={dc.signals.customer360.score}
          weight={20}
          body={
            <div className="col-span-full space-y-2">
              <div className="text-sm text-neutral-400">
                Across the top {dc.signals.customer360.sampledAccounts} accounts by recent activity, the
                average completeness is{' '}
                <span className="text-crux-light tabular-nums">
                  {dc.signals.customer360.averageAxes.toFixed(1)} / 7 axes
                </span>
                .
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-7 gap-2">
                {dc.signals.customer360.perAxis.map((a) => {
                  const pct =
                    dc.signals.customer360.sampledAccounts > 0
                      ? (a.populated / dc.signals.customer360.sampledAccounts) * 100
                      : 0;
                  return (
                    <div key={a.axis} className="bg-surface-2 border border-border rounded-md p-2">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 truncate">
                        {a.axis}
                      </div>
                      <div className="text-sm text-crux-light tabular-nums mt-1">
                        {a.populated} / {dc.signals.customer360.sampledAccounts}
                      </div>
                      <div className="text-[10px] text-neutral-500">{pct.toFixed(0)}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          }
          upsell="Data Cloud: unified profile"
        />
      </div>
    </div>
  );
}

function ScoreTile({ label, score, icon }: { label: string; score: number; icon: React.ReactNode }) {
  return (
    <div className="bg-surface-2 border border-border rounded-md p-3">
      <div className="flex items-center justify-center gap-1 text-neutral-500 text-[11px] uppercase tracking-widest">
        {icon}
        {label}
      </div>
      <div className="text-lg font-semibold text-crux-light tabular-nums mt-1">{score}</div>
      <div className="text-[10px] text-neutral-500">/ 20</div>
    </div>
  );
}

function SignalCard({
  title,
  score,
  weight,
  body,
  upsell,
}: {
  title: string;
  score: number;
  weight: number;
  body: React.ReactNode;
  upsell: string;
}) {
  const tone = score >= weight * 0.75 ? 'healthy' : score >= weight * 0.5 ? 'medium' : 'critical';
  return (
    <div className="bg-surface-1 border border-border rounded-lg p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold text-crux-light">{title}</span>
            <RiskBadge tone={tone}>
              {score} / {weight}
            </RiskBadge>
          </div>
          <div className="text-xs text-neutral-500 mt-0.5" data-internal-only="true">Upsell angle: {upsell}</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">{body}</div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-crux-light tabular-nums mt-0.5">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

